import type {
  LegacySpuerhundExport,
  SpuerhundExport,
  StoredHistoryEntry,
  StoredQuery,
  StoredSchemaCache,
  StoredServer,
} from "./persistence";

export interface ImportMergePlan {
  readonly serversToUpsert: StoredServer[];
  readonly replacedServerIds: string[];
  readonly queriesToInsert: StoredQuery[];
  readonly schemaCacheToUpsert: StoredSchemaCache[];
  readonly historyToInsert: Array<Omit<StoredHistoryEntry, "id">>;
}

function normalizeServerName(name: string): string {
  return name.trim().toLocaleLowerCase();
}

function nextUniqueId(preferredId: string, usedIds: Set<string>, makeId: () => string): string {
  const trimmed = preferredId.trim();
  if (trimmed && !usedIds.has(trimmed)) {
    usedIds.add(trimmed);
    return trimmed;
  }

  let generated = makeId();
  while (usedIds.has(generated)) {
    generated = makeId();
  }
  usedIds.add(generated);
  return generated;
}

export function buildImportMergePlan(input: {
  existingServers: StoredServer[];
  existingQueries: StoredQuery[];
  importedData: SpuerhundExport | LegacySpuerhundExport;
  now: string;
  makeId: () => string;
}): ImportMergePlan {
  const existingByName = new Map<string, StoredServer>();
  const usedServerIds = new Set<string>();
  let maxSortOrder = -1;

  for (const server of input.existingServers) {
    usedServerIds.add(server.id);
    maxSortOrder = Math.max(maxSortOrder, server.sortOrder);
    const normalizedName = normalizeServerName(server.name);
    if (normalizedName && !existingByName.has(normalizedName)) {
      existingByName.set(normalizedName, server);
    }
  }

  const importedServerIdToTargetId = new Map<string, string>();
  const serversToUpsert: StoredServer[] = [];
  const replacedServerIds = new Set<string>();
  let nextSortOrder = maxSortOrder + 1;

  for (const importedServer of input.importedData.servers) {
    const normalizedName = normalizeServerName(importedServer.name);
    const matchedServer = normalizedName ? existingByName.get(normalizedName) : undefined;

    if (matchedServer) {
      importedServerIdToTargetId.set(importedServer.id, matchedServer.id);
      replacedServerIds.add(matchedServer.id);

      const mergedServer: StoredServer = {
        ...matchedServer,
        ...importedServer,
        id: matchedServer.id,
        sortOrder: matchedServer.sortOrder,
        createdAt: matchedServer.createdAt,
        updatedAt: input.now,
      };

      serversToUpsert.push(mergedServer);
      existingByName.set(normalizedName, mergedServer);
      continue;
    }

    const targetId = nextUniqueId(importedServer.id, usedServerIds, input.makeId);
    importedServerIdToTargetId.set(importedServer.id, targetId);

    const appendedServer: StoredServer = {
      ...importedServer,
      id: targetId,
      sortOrder: nextSortOrder++,
    };

    serversToUpsert.push(appendedServer);
    if (normalizedName) {
      existingByName.set(normalizedName, appendedServer);
    }
  }

  const replacedServerIdSet = new Set(replacedServerIds);
  const queryIdsInUse = new Set(
    input.existingQueries
      .filter((query) => !replacedServerIdSet.has(query.serverId))
      .map((query) => query.id),
  );

  const importedQueryIdToTargetId = new Map<string, string>();
  const queriesToInsert: StoredQuery[] = [];

  for (const importedQuery of input.importedData.queries) {
    const targetServerId = importedServerIdToTargetId.get(importedQuery.serverId);
    if (!targetServerId) continue;

    const targetQueryId = nextUniqueId(importedQuery.id, queryIdsInUse, input.makeId);
    importedQueryIdToTargetId.set(importedQuery.id, targetQueryId);

    queriesToInsert.push({
      ...importedQuery,
      id: targetQueryId,
      serverId: targetServerId,
    });
  }

  const schemaCacheToUpsert: StoredSchemaCache[] = [];
  for (const cacheEntry of input.importedData.schemaCache) {
    const targetServerId = importedServerIdToTargetId.get(cacheEntry.serverId);
    if (!targetServerId) continue;
    schemaCacheToUpsert.push({
      ...cacheEntry,
      serverId: targetServerId,
    });
  }

  const historyToInsert: Array<Omit<StoredHistoryEntry, "id">> = [];
  for (const historyEntry of input.importedData.history) {
    const targetServerId = importedServerIdToTargetId.get(historyEntry.serverId);
    if (!targetServerId) continue;

    const { id: _ignored, ...rest } = historyEntry;
    const targetQueryId = historyEntry.queryId
      ? importedQueryIdToTargetId.get(historyEntry.queryId)
      : undefined;

    historyToInsert.push({
      ...rest,
      serverId: targetServerId,
      headers: typeof historyEntry.headers === "string" ? historyEntry.headers : "{}",
      ...(targetQueryId ? { queryId: targetQueryId } : {}),
    });
  }

  return {
    serversToUpsert,
    replacedServerIds: [...replacedServerIds],
    queriesToInsert,
    schemaCacheToUpsert,
    historyToInsert,
  };
}
